import { computed, inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { doc, Firestore, setDoc } from '@angular/fire/firestore';
import { catchError, EMPTY, exhaustMap, from, interval, Observable, startWith, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { buildStaffOnlineWritePayload } from 'src/pms/shared/components/staff-online-rota-view/staff-online-rota-view.utils';
import { StaffProfileStore } from 'src/pms/stores/staff-profile/staff-profile.store';
import { AuthenticationService } from 'src/shared/services/auth/auth_service';
import { bookinginternal } from 'src/shared/services/client/client';

const INTERVAL_TO_SAY_IM_ONLINE_MS = 15_000;
const STAFF_ONLINE_COLLECTION = 'staffonline';

/**
 * Writes the current staff member's online presence to Firestore on a fixed interval.
 */
@Injectable()
export class StaffOnlineWriteService {
  private readonly firestore = inject(Firestore);
  private readonly staffProfileStore = inject(StaffProfileStore);
  private readonly authenticationService = inject(AuthenticationService);

  private readonly activeProfile = computed(() => {
    const profile = this.staffProfileStore.profile();
    const loggedIn = this.authenticationService.loggedIn();
    return profile && loggedIn ? profile : undefined;
  });

  readonly heartbeat = rxResource({
    params: this.activeProfile,
    stream: ({ params: profile, abortSignal }) => this.writeHeartbeatStream(profile, abortSignal),
  });

  private writeHeartbeatStream(
    profile: bookinginternal.GetStaffProfileResponse,
    abortSignal: AbortSignal
  ): Observable<void> {
    return interval(INTERVAL_TO_SAY_IM_ONLINE_MS).pipe(
      startWith(0),
      exhaustMap(() => this.updateFirestoreStaffOnlineStatus(profile)),
      takeUntil(abortWhen(abortSignal))
    );
  }

  private updateFirestoreStaffOnlineStatus(profile: bookinginternal.GetStaffProfileResponse): Observable<void> {
    const staffRef = doc(this.firestore, STAFF_ONLINE_COLLECTION, profile.id ?? '');
    const payload = buildStaffOnlineWritePayload(
      profile,
      environment.environment,
      this.authenticationService.jwt()?.roles
    );

    return from(setDoc(staffRef, payload, { merge: true })).pipe(
      catchError((error) => {
        console.error('Error updating staff online status:', error);
        return EMPTY;
      })
    );
  }
}

function abortWhen(abortSignal: AbortSignal): Observable<never> {
  return new Observable((subscriber) => {
    if (abortSignal.aborted) {
      subscriber.complete();
      return;
    }

    const onAbort = () => subscriber.complete();
    abortSignal.addEventListener('abort', onAbort);
    return () => abortSignal.removeEventListener('abort', onAbort);
  });
}
